<template>
  <section class="bg-[#FBFBFD] font-sans text-[#1D1D1F] overflow-hidden antialiased selection:bg-blue-100">
    
    <div class="container mx-auto px-6 lg:px-20 py-24 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
      
      <div class="relative group" data-aos="zoom-out-up" data-aos-duration="1200">
        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-4 pt-8" data-aos="fade-up" data-aos-delay="200">
            <img :src="require('@/assets/images/students.jpg')" class="apple-image aspect-square" />
            <img :src="require('@/assets/images/classroom.jpeg')" class="apple-image aspect-[4/5]" />
          </div>
          <div class="space-y-4" data-aos="fade-up" data-aos-delay="400">
            <img :src="require('@/assets/images/children.png')" class="apple-image aspect-[4/5]" />
            <img :src="require('@/assets/images/image_test.jpg')" class="apple-image aspect-square" />
          </div>
        </div>
        <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div class="bg-white/60 backdrop-blur-2xl text-blue-700 text-sm font-bold px-8 py-4 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white/40 tracking-tight animate-float">
            NovaLingua Excellence
          </div>
        </div>
      </div>

      <div class="space-y-8" data-aos="fade-left">
        <h2 class="text-4xl md:text-5xl font-bold leading-tight tracking-tighter text-gray-900">
          Apprenez les langues autrement, <span class="bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">progressez vers vos rêves.</span>
        </h2>

        <div class="space-y-6 text-lg text-gray-500 font-light leading-relaxed">
          <p>Bienvenue au <span class="font-semibold text-gray-800">Centre NovaLingua</span>, un espace d’apprentissage moderne situé à Bafoussam et Dschang.</p>
        </div>

        <button @click="showGallery = !showGallery" 
          class="flex items-center gap-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-900 px-6 py-3 rounded-full shadow-sm transition-all active:scale-90 font-medium">
          <span :class="['w-2 h-2 rounded-full transition-colors duration-500', showGallery ? 'bg-red-500' : 'bg-blue-600']"></span>
          {{ showGallery ? 'Masquer' : 'Découvrir nos espaces' }}
        </button>
      </div>
    </div>

    <transition name="ios-launch">
      <div v-show="showGallery" class="container mx-auto px-6 lg:px-20 pb-24">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="(card, i) in imageCards" :key="i" 
            class="ios-card group">
            <div class="h-48 overflow-hidden">
              <img :src="require(`@/assets/images/${card.src}`)" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1000ms] ease-out" />
            </div>
            <div class="p-6">
              <h4 class="font-bold text-gray-900 mb-1">{{ card.title }}</h4>
              <p class="text-xs text-gray-400 font-medium uppercase tracking-widest">{{ card.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div class="bg-white py-24">
      <div class="container mx-auto px-6 lg:px-20">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <router-link :to="{name: 'FormationView'}" v-for="(lang, index) in languages" :key="index"
            class="group bg-[#F5F5F7] p-10 rounded-[2.5rem] transition-all duration-500 hover:bg-white hover:shadow-[0_30px_60px_rgba(0,0,0,0.1)] active:scale-95">
            <div class="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm mb-8 group-hover:bg-blue-600 transition-colors">
              <svg class="w-6 h-6 text-blue-600 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" /></svg>
            </div>
            <h4 class="text-xl font-bold text-gray-900 mb-2">{{ lang.name }}</h4>
            <p class="text-gray-500 text-sm font-light">{{ lang.desc }}</p>
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import AOS from 'aos'

onMounted(() => {
  AOS.init({ duration: 800, easing: 'ease-out', once: true })
})

const showGallery = ref(true)
// ...vos données (imageCards, languages, steps, header) restent identiques...
const imageCards = ref([
  { src: 'students.jpg', title: 'Étudiants en classe', desc: "Ambiance d'apprentissage collaborative." },
  { src: 'children.png', title: 'Atelier interactif', desc: 'Activités ludiques pour tous.' },
  { src: 'classroom.jpeg', title: 'Salles modernes', desc: 'Espaces équipés haut de gamme.' },
  { src: 'image_test.jpg', title: 'Suivi individuel', desc: 'Accompagnement personnalisé.' }
])

const languages = ref([
  { name: 'Anglais', desc: 'Maîtrisez la langue des affaires mondiales.' },
  { name: 'Allemand', desc: 'Préparez votre avenir académique en Allemagne.' },
  { name: 'Français', desc: 'Perfectionnez votre éloquence et vos écrits.' },
  { name: 'Italien', desc: 'Explorez la langue de l’art et de la culture.' }
])

const steps = ref([
  { title: 'Dossier', text: 'Constitution et démarches administratives.' },
  { title: 'Partenariat', text: 'Mise en relation directe avec l’étranger.' },
  { title: 'Stages', text: 'Encadrement professionnel avant départ.' },
  { title: 'Langue', text: 'Atteignez le niveau B2 minimum requis.' },
  { title: 'Intégration', text: 'Accueil et suivi dans votre pays d’accueil.' }
])

const header = ref({ phone: '+237 656 53 12 25' })
</script>

<style scoped>
/* Courbe de transition iOS (Fluid & Snappy) */
.ios-launch-enter-active {
  transition: all 0.8s cubic-bezier(0.32, 0.72, 0, 1);
}
.ios-launch-leave-active {
  transition: all 0.5s cubic-bezier(0.32, 0.72, 0, 1);
}

.ios-launch-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(40px);
  filter: blur(15px);
}
.ios-launch-leave-to {
  opacity: 0;
  transform: scale(0.95);
  filter: blur(10px);
}

/* Éléments de design Apple */
.apple-image {
  @apply w-full object-cover rounded-[2.5rem] shadow-[0_10px_30px_rgba(0,0,0,0.05)] border border-white/20 transition-transform duration-700 hover:scale-[1.02];
}

.ios-card {
  @apply bg-white rounded-[2.2rem] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)] transition-all duration-500 border border-gray-100;
}

/* Animation de flottement pour le badge */
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
.animate-float {
  animation: float 4s ease-in-out infinite;
}

/* Typographie système Apple */
.font-sans {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif;
  letter-spacing: -0.015em;
}
</style>